import { useEffect, useMemo, useState } from "react";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Warning, ChatCircle, User, MagnifyingGlass, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const priColor = {
  low: "border-muted-foreground/40 text-muted-foreground",
  medium: "border-primary/40 text-primary",
  high: "border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]",
  critical: "border-[hsl(var(--destructive)/0.4)] text-[hsl(var(--destructive))]",
};
const statusColor = {
  open: "text-[hsl(var(--warning))]",
  in_progress: "text-primary",
  resolved: "text-[hsl(var(--success))]",
  closed: "text-muted-foreground",
};
const SLA = { low: 72, medium: 48, high: 24, critical: 4 };

function slaState(t) {
  if (!t?.created_at || ["resolved", "closed"].includes(t?.status)) return { breach: false, remainingH: null };
  const created = new Date(t.created_at).getTime();
  const hours = t.sla_hours || SLA[t.priority] || 48;
  const deadline = created + hours * 3600 * 1000;
  const remainingH = (deadline - Date.now()) / 3600000;
  return { breach: remainingH < 0, remainingH };
}

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [dash, setDash] = useState({ open: 0, resolved: 0, total: 0, sla_breach: 0, by_priority: {} });
  const [subs, setSubs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [q, setQ] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ subscriber_id: "", subject: "", description: "", priority: "medium", assigned_to: "" });

  const load = async () => {
    const [a, b, c] = await Promise.all([http.get("/tickets"), http.get("/subscribers"), http.get("/tickets/dashboard")]);
    setTickets(a.data); setSubs(b.data); setDash(c.data);
  };
  useEffect(() => { load(); }, []);

  const subMap = useMemo(() => Object.fromEntries(subs.map((s) => [s.id, s])), [subs]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await http.post("/tickets", { ...form, subscriber_id: form.subscriber_id || null, assigned_to: form.assigned_to || null });
      toast.success("Ticket opened");
      setAddOpen(false);
      setForm({ subscriber_id: "", subject: "", description: "", priority: "medium", assigned_to: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    await http.patch(`/tickets/${id}`, { status });
    if (detail?.id === id) setDetail({ ...detail, status });
    load();
  };
  const setAssignee = async (id, assigned_to) => {
    await http.patch(`/tickets/${id}`, { assigned_to });
    if (detail?.id === id) setDetail({ ...detail, assigned_to });
    load();
  };
  const addComment = async () => {
    if (!comment.trim() || !detail) return;
    try {
      const { data } = await http.post(`/tickets/${detail.id}/comments`, { text: comment });
      setDetail(data);
      setComment("");
      load();
    } catch { toast.error("Failed to add comment"); }
  };

  const filtered = tickets.filter((t) => {
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (q && ![t.ticket_no, t.subject, t.description, subMap[t.subscriber_id]?.full_name].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6 fade-up" data-testid="support-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Field ops</div>
          <h1 className="font-display font-bold text-4xl mt-1">Support tickets</h1>
        </div>
        <Button data-testid="new-ticket-btn" onClick={() => setAddOpen(true)} className="rounded-sm bg-primary hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New ticket</Button>
      </div>

      {/* Dashboard header */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Open" value={dash.open} testid="k-open" />
        <Kpi label="Resolved" value={dash.resolved} testid="k-resolved" />
        <Kpi label="SLA breach" value={dash.sla_breach} testid="k-sla" tone={dash.sla_breach > 0 ? "danger" : "muted"} icon={dash.sla_breach > 0 ? Warning : null} />
        {["critical", "high", "medium", "low"].map((p) => (
          <Kpi key={p} label={`${p}`} value={dash.by_priority?.[p] || 0} testid={`k-pri-${p}`} tone={p === "critical" ? "danger" : p === "high" ? "warn" : "muted"} />
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input placeholder="Search ticket, subject, subscriber…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-secondary" data-testid="ticket-search" />
        </div>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="h-9 bg-secondary border border-border px-3 text-sm rounded-sm" data-testid="ticket-priority-filter">
          <option value="all">All priorities</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 bg-secondary border border-border px-3 text-sm rounded-sm" data-testid="ticket-status-filter">
          <option value="all">All statuses</option><option value="open">Open</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
        </select>
        <div className="text-xs font-mono text-muted-foreground ml-auto">{filtered.length} / {tickets.length}</div>
      </div>

      {/* Ticket list */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="border-b border-border">
            <th className="uppercase-label text-left py-2 px-3">Ticket</th>
            <th className="uppercase-label text-left py-2 px-3">Subject</th>
            <th className="uppercase-label text-left py-2 px-3">Subscriber</th>
            <th className="uppercase-label text-left py-2 px-3">Priority</th>
            <th className="uppercase-label text-left py-2 px-3">Status</th>
            <th className="uppercase-label text-left py-2 px-3">Assignee</th>
            <th className="uppercase-label text-left py-2 px-3">SLA</th>
          </tr></thead>
          <tbody>
            {filtered.map((t) => {
              const sla = slaState(t);
              return (
                <tr key={t.id} onClick={() => setDetail(t)} data-testid={`ticket-row-${t.id}`} className="border-b border-border/50 hover:bg-accent/20 cursor-pointer">
                  <td className="py-2 px-3 font-mono text-xs">{t.ticket_no}</td>
                  <td className="py-2 px-3">{t.subject}<div className="text-[10px] text-muted-foreground font-mono mt-0.5">{fmtDate(t.created_at)}</div></td>
                  <td className="py-2 px-3 text-xs">{subMap[t.subscriber_id]?.full_name || "—"}</td>
                  <td className="py-2 px-3"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm ${priColor[t.priority]}`}>{t.priority}</span></td>
                  <td className="py-2 px-3"><span className={`text-xs uppercase font-mono ${statusColor[t.status]}`}>{t.status.replace("_", " ")}</span></td>
                  <td className="py-2 px-3 text-xs font-mono text-muted-foreground">{t.assigned_to || <span className="italic">unassigned</span>}</td>
                  <td className="py-2 px-3 text-xs font-mono">{sla.remainingH === null ? "—" : sla.breach ? <span className="text-[hsl(var(--destructive))] inline-flex items-center gap-1"><Warning size={12} /> breach</span> : `${sla.remainingH.toFixed(1)}h left`}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No tickets match.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Add ticket dialog (dedicated form) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Open a new ticket</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div><Label className="uppercase-label">Subject</Label><Input required data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="uppercase-label">Description</Label><Textarea required data-testid="ticket-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={4} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="uppercase-label">Subscriber (optional)</Label>
                <Select value={form.subscriber_id} onValueChange={(v) => setForm({ ...form, subscriber_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ticket-sub"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-64">{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.username}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase-label">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low · 72h SLA</SelectItem>
                    <SelectItem value="medium">Medium · 48h SLA</SelectItem>
                    <SelectItem value="high">High · 24h SLA</SelectItem>
                    <SelectItem value="critical">Critical · 4h SLA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="uppercase-label">Assign to</Label><Input placeholder="engineer@sns.local (optional)" data-testid="ticket-assignee" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="mt-1.5 font-mono" /></div>
            <DialogFooter><Button type="submit" data-testid="ticket-submit" className="rounded-sm">Open ticket</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket detail + comments */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="ticket-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{detail.ticket_no}</span>
                  {detail.subject}
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm ${priColor[detail.priority]}`}>{detail.priority}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div><div className="uppercase-label">Subscriber</div><div className="mt-1">{subMap[detail.subscriber_id]?.full_name || "—"}</div></div>
                  <div><div className="uppercase-label">Assignee</div><Input value={detail.assigned_to || ""} onChange={(e) => setDetail({ ...detail, assigned_to: e.target.value })} onBlur={() => setAssignee(detail.id, detail.assigned_to || null)} placeholder="unassigned" data-testid="detail-assignee" className="mt-1 h-8 font-mono text-xs" /></div>
                  <div><div className="uppercase-label">Opened</div><div className="mt-1 font-mono">{fmtDate(detail.created_at)}</div></div>
                </div>
                <div className="text-sm p-3 border border-border bg-secondary/30 whitespace-pre-wrap">{detail.description}</div>

                <div className="flex items-center gap-2 flex-wrap">
                  {["open", "in_progress", "resolved", "closed"].map((s) => (
                    <button key={s} data-testid={`detail-status-${s}`} onClick={() => setStatus(detail.id, s)} className={`text-[10px] uppercase tracking-wide px-2 py-1 border rounded-sm transition-colors ${detail.status === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{s.replace("_", " ")}</button>
                  ))}
                </div>

                {/* Comments thread */}
                <div className="border-t border-border pt-4">
                  <div className="uppercase-label flex items-center gap-2 mb-3"><ChatCircle size={14} /> Comments ({(detail.comments || []).length})</div>
                  <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="detail-comments">
                    {(detail.comments || []).length === 0 && <div className="text-xs text-muted-foreground italic">No comments yet.</div>}
                    {(detail.comments || []).map((c, i) => (
                      <div key={i} className="border border-border/60 p-3 bg-secondary/20">
                        <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><User size={10} /> {c.author}</span>
                          <span>{fmtDate(c.ts)}</span>
                        </div>
                        <div className="mt-1 text-sm whitespace-pre-wrap">{c.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-end gap-2">
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Type a comment…" rows={2} data-testid="detail-comment-input" />
                    <Button onClick={addComment} data-testid="detail-comment-submit" className="rounded-sm bg-primary hover:bg-primary/90"><PaperPlaneRight size={14} className="mr-1" /> Send</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone = "muted", testid, icon: Icon }) {
  const toneClass = tone === "danger" ? "text-[hsl(var(--destructive))]" : tone === "warn" ? "text-[hsl(var(--warning))]" : "text-foreground";
  return (
    <div className="border border-border bg-card p-3" data-testid={testid}>
      <div className="uppercase-label capitalize flex items-center gap-1">{Icon && <Icon size={12} />} {label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
